import { Spacer } from 'app/layouts/generic';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Button } from 'app/shared/button';
import { PageLoader } from 'app/shared/loader';
import { MultiSelect } from 'app/shared/select';
import { generateRandomNumber } from 'app/shared/utils/number';
import { useContactFileService } from 'hooks/file-config';
import { useAudienceService } from 'hooks/users/audience';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './query.module.css';
import { QueryRule } from './rule';
import { processCustomAttribute } from 'app/shared/utils/text';

const OPERATORS = [
  { label: 'OR', value: '$or' },
  { label: 'AND', value: '$and' }
];

const DEFAULT_PROPERTIES = {
  date_of_birth: { label: 'Date of Birth', value: 'date_of_birth', kind: 'default' },
  email: { label: 'Email Address', value: 'email', kind: 'default' },
  firstname: { label: 'First Name', value: 'firstname', kind: 'default' },
  lastname: { label: 'Last Name', value: 'lastname', kind: 'default' },
  //
  event: { label: 'Events Invoked', value: 'events', kind: 'default' },
  last_action: { label: 'Last Action', value: 'last_action', kind: 'custom' },
  last_action_time: { label: 'Last Action Time', value: 'last_action_time', kind: 'custom' }
};

export const AudienceQueryBuilder = ({ onQueryChange }) => {
  const { id: audience_id } = useParams();
  const { fetchAudienceById, updateAudience } = useAudienceService();
  const { fetchMapping } = useContactFileService();

  const [audience, setAudience] = useState({});
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('view');
  const [operator, setOperator] = useState(OPERATORS[0]);
  const [properties, setProperties] = useState([]);
  const [queries, setQueries] = useState({});

  useEffect(() => {
    if (!audience_id) return;
    setLoading(true);
    fetchAudienceById(audience_id)
      .then(({ audience }) => {
        const { query } = audience;
        setAudience(audience);

        if (query) {
          setOperator(
            () => OPERATORS.find((operator) => operator.value === query.operator) || OPERATORS[0]
          );

          query.rules?.forEach((rule) => {
            const key = `query:${rule._id || generateRandomNumber()}`;
            setQueries((curr_queries) => ({ ...curr_queries, [key]: { ...rule, key } }));
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });

    fetchMapping().then(({ mapping }) => {
      const { custom_fields } = mapping || {};
      const parsed_fields = (custom_fields || []).reduce(
        (sac, field) => ({
          ...sac,
          [field]: { label: processCustomAttribute(`${field}`), value: field, kind: 'custom' }
        }),
        {}
      );

      const allowed_properties = { ...parsed_fields, ...DEFAULT_PROPERTIES };
      setProperties(Object.values(allowed_properties));
    });
  }, [audience_id]);

  const handleRuleAddition = () => {
    const key = `query:${generateRandomNumber()}`;
    setQueries((curr_queries) => ({ ...curr_queries, [key]: { key } }));
  };

  const handleRuleDeletion = (key) => {
    const new_queries = { ...queries };
    delete new_queries[key];
    setQueries(() => new_queries);
  };

  const handleQueryChange = (query = {}) => {
    setQueries((queries) => ({
      ...queries,
      [query.key]: query
    }));
  };

  const submit = async () => {
    const valid_queries = Object.values(queries).filter((query) => !!query.needle);

    const data = {
      query: { operator: operator.value, rules: valid_queries || [] }
    };

    const result = await updateAudience(audience_id, { data });
    if (result) {
      onQueryChange();
      setMode(() => 'view');

      toast.success('Query saved.');
    }
  };

  return (
    <div className={styles.wrapper}>
      <header>
        <GridRow num_of_columns={4}>
          <GridColumn>
            <MultiSelect
              options={OPERATORS}
              value={operator}
              onChange={setOperator}
              isSorted={false}
              disabled={mode === 'view'}
            />
          </GridColumn>
          <GridColumn />
          <GridColumn>
            {mode === 'edit' && (
              <Button type="secondary" text="Cancel" onClick={() => setMode('view')} />
            )}
          </GridColumn>
          <GridColumn>
            {mode === 'edit' ? (
              <Button text="Save Query" onClick={submit} />
            ) : (
              <Button
                text={Object.keys(queries).length > 0 ? 'Edit Query' : 'Add Query'}
                icon_name={Object.keys(queries).length > 0 ? 'edit' : 'add'}
                type="secondary"
                onClick={() => setMode('edit')}
                disabled={audience.type === 'system'}
              />
            )}
          </GridColumn>
        </GridRow>
      </header>
      <Spacer multiple={6} />
      {loading ? (
        <PageLoader />
      ) : (
        <section>
          {Object.keys(queries).map((key, index) => (
            <QueryRule
              key={key}
              index={index}
              data={queries[key]}
              properties={properties}
              mode={mode}
              onChange={handleQueryChange}
              onDelete={handleRuleDeletion}
            />
          ))}
        </section>
      )}
      {mode === 'edit' && (
        <GridRow num_of_columns={12}>
          <GridColumn span={3}>
            <Button
              className={styles.linkBtn}
              icon_name="add"
              text="Add Rule"
              onClick={handleRuleAddition}
            />
          </GridColumn>
        </GridRow>
      )}
    </div>
  );
};
